/**
 * Clerk-specific authentication token provider
 * Integrates with Clerk's authentication system to provide tokens
 */

import type { AuthTokenProvider } from '../auth-provider'

export interface ClerkAuthProviderConfig {
  /**
   * Optional Clerk instance - if not provided, will try to get from global context
   */
  clerk?: any
  /**
   * Token template to use (default: 'session')
   * Use 'session' for default Clerk session tokens, or specify a custom JWT template name
   * Can be 'session', 'organization', or custom template name from Clerk dashboard
   */
  tokenTemplate?: string
}

/**
 * Clerk authentication token provider implementation
 */
export class ClerkAuthProvider implements AuthTokenProvider {
  private clerk: any
  private tokenTemplate: string
  private authStateCallbacks: Set<(isAuthenticated: boolean) => void> = new Set()

  constructor(config: ClerkAuthProviderConfig = {}) {
    this.clerk = config.clerk
    this.tokenTemplate = config.tokenTemplate || 'session'
  }

  async getToken(): Promise<string | null> {
    try {
      // If clerk instance is not provided, try to get it from global context
      const clerk = this.clerk || this.getClerkFromGlobal()

      if (!clerk) {
        console.warn('Clerk instance not available')
        return null
      }

      // Get the session token
      // If tokenTemplate is 'session', use default behavior without template parameter
      const token =
        this.tokenTemplate === 'session'
          ? await clerk.session?.getToken()
          : await clerk.session?.getToken({ template: this.tokenTemplate })

      return token || null
    } catch (error) {
      console.error('Failed to get Clerk token:', error)
      return null
    }
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      const clerk = this.clerk || this.getClerkFromGlobal()

      if (!clerk) {
        return false
      }

      return !!clerk.user
    } catch (error) {
      console.error('Failed to check Clerk authentication status:', error)
      return false
    }
  }

  onAuthStateChange(callback: (isAuthenticated: boolean) => void): () => void {
    this.authStateCallbacks.add(callback)

    // Set up Clerk auth state listener if available
    const clerk = this.clerk || this.getClerkFromGlobal()

    if (clerk && typeof clerk.addListener === 'function') {
      const handleAuthChange = () => {
        const isAuthenticated = !!clerk.user
        this.authStateCallbacks.forEach(cb => cb(isAuthenticated))
      }

      clerk.addListener('user', handleAuthChange)
      clerk.addListener('session', handleAuthChange)

      return () => {
        this.authStateCallbacks.delete(callback)
        if (typeof clerk.removeListener === 'function') {
          clerk.removeListener('user', handleAuthChange)
          clerk.removeListener('session', handleAuthChange)
        }
      }
    }

    // Fallback: return a no-op unsubscribe function
    return () => {
      this.authStateCallbacks.delete(callback)
    }
  }

  /**
   * Try to get Clerk instance from global context
   * This works when Clerk is initialized globally
   */
  private getClerkFromGlobal(): any {
    if (typeof window !== 'undefined') {
      // Try to get from window object (common pattern)
      return (window as any).__clerk
    }
    return null
  }

  /**
   * Update the Clerk instance (useful for dynamic updates)
   */
  setClerkInstance(clerk: any): void {
    this.clerk = clerk
  }
}

/**
 * Create a Clerk auth provider with the given configuration
 */
export function createClerkAuthProvider(config: ClerkAuthProviderConfig = {}): ClerkAuthProvider {
  return new ClerkAuthProvider(config)
}
